// DocumentPreviewCard.jsx — DL-049 "Full" / "Cover" document preview cards.
//
// Renders a single document as either the Full (first-page preview) card or the
// Cover (lavender treatment) card, per the pdf-viewer-elevated deck
// (01-first-page-preview.png). Only reached when PDF_PREVIEW_STYLES_ENABLED is
// ON and the block's previewStyle is "full" | "cover" (see index.jsx); when the
// flag is OFF this component is never rendered and the block is byte-identical
// to today.
//
// Reuses the already-built + verified in-app viewer pieces:
//   - PdfFirstPagePreview → renders the real PDF first page (falls back to the
//     type icon while loading, on error, on oversize, or for non-PDF files);
//   - PdfViewerModal      → the in-app "Open" experience for PDFs;
//   - isPdfUrl            → the PDF gate + SSRF/scheme guard.
// Non-PDF documents keep the legacy open-externally behaviour on Open.

import React, { useState } from "react";
import styled from "styled-components";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { colors } from "../../../../utils";
import { goToUrl } from "../../../../utils/helpers";
import { fileIcons } from "../../../../constants/documentIcon";
import { ASSETS } from "../../../../utils/config";
import {
  isPdfUrl,
  PdfViewerModal,
  PdfFirstPagePreview,
} from "../../../InAppDocViewer";
import TextView from "../../../TextView";
import { PREVIEW_STYLES, getDocTypeLabel, getDocTypeChip } from "./documentPreview";

const FACSIMILE_W = 64; // page-thumbnail render width (px)

// ---- Full card ------------------------------------------------------------
const FullCard = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px;
  border: 1px solid ${colors.PrimaryPurple300 || "#CBB4FE"};
  border-radius: 12px;
  background: ${colors.white};
  box-shadow: 0 1px 3px rgba(18, 48, 81, 0.06),
    0 6px 18px rgba(155, 90, 248, 0.08);
  transition: transform 160ms ease, box-shadow 160ms ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(18, 48, 81, 0.1),
      0 10px 24px rgba(155, 90, 248, 0.12);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &:hover {
      transform: none;
    }
  }
`;

// ---- Cover card -----------------------------------------------------------
const CoverCard = styled.div`
  position: relative;
  border-radius: 12px;
  background: linear-gradient(
    160deg,
    ${colors.PrimaryPurple100 || "#EFE9FE"} 0%,
    ${colors.white} 78%
  );
  border: 1px solid ${colors.PrimaryPurple100 || "#EFE9FE"};
  padding: 16px;
  box-shadow: 0 1px 3px rgba(18, 48, 81, 0.06),
    0 6px 18px rgba(155, 90, 248, 0.1);
  transition: transform 160ms ease, box-shadow 160ms ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(18, 48, 81, 0.1),
      0 10px 24px rgba(155, 90, 248, 0.14);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &:hover {
      transform: none;
    }
  }
`;

const Chip = styled.span`
  display: inline-block;
  font-family: "DM Sans", sans-serif;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.4px;
  color: ${colors.white};
  background: ${colors.PrimaryPurple || "#9B5AF8"};
  border-radius: 6px;
  padding: 3px 7px;
`;

const OpenButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: "DM Sans", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${colors.white};
  background: ${colors.PrimaryPurple || "#9B5AF8"};
  border: none;
  border-radius: 20px;
  padding: 8px 16px;
  cursor: pointer;
  transition: background 160ms ease;

  &:hover {
    background: ${colors.PrimaryPurple700 || "#8126DB"};
  }
  &:focus-visible {
    outline: 2px solid ${colors.PrimaryPurple700 || "#8126DB"};
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const FacsimileWrap = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: flex-start;
`;

const FallbackIcon = styled.img`
  width: ${FACSIMILE_W}px;
  height: ${Math.round((FACSIMILE_W * 40) / 34)}px;
  object-fit: contain;
  border-radius: 6px;
`;

const CoverStage = styled.div`
  display: flex;
  justify-content: center;
  padding: 10px 0 16px;
`;

const CoverStageInner = styled.div`
  background: ${colors.white};
  border-radius: 8px;
  padding: 8px;
  box-shadow: 0 2px 10px rgba(18, 48, 81, 0.12);
`;

const Meta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const CoverBottom = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const OpenLabel = (isPdf) => (isPdf ? "Open PDF" : "Open");

const titleEllipsis = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

// Shared Open action — identical wiring in both card bodies.
function OpenAction({ isPdf, title, onOpen }) {
  return (
    <OpenButton
      type="button"
      onClick={onOpen}
      aria-label={`${OpenLabel(isPdf)}: ${title}`}
    >
      <OpenInNewIcon style={{ fontSize: 16 }} />
      {OpenLabel(isPdf)}
    </OpenButton>
  );
}

// Cover-style card (lavender gradient + type chip). Deck 01: "Cover style".
function CoverBody({ chip, facsimile, title, typeLabel, isPdf, onOpen }) {
  return (
    <CoverCard>
      <Chip>{chip}</Chip>
      <CoverStage>
        <CoverStageInner>{facsimile}</CoverStageInner>
      </CoverStage>
      <CoverBottom>
        <Meta>
          <TextView variant="subtitle" style={titleEllipsis}>
            {title}
          </TextView>
          <TextView variant="text" color={colors.textGrey}>
            {typeLabel}
          </TextView>
        </Meta>
        <OpenAction isPdf={isPdf} title={title} onOpen={onOpen} />
      </CoverBottom>
    </CoverCard>
  );
}

// Full card (white card + first-page facsimile). Deck 01: "First-page preview".
function FullBody({ facsimile, title, typeLabel, isPdf, onOpen }) {
  return (
    <FullCard>
      <FacsimileWrap>{facsimile}</FacsimileWrap>
      <Meta style={{ flex: 1 }}>
        <TextView variant="subtitle" style={titleEllipsis}>
          {title}
        </TextView>
        <TextView
          variant="text"
          color={colors.textGrey}
          style={{ marginBottom: 6 }}
        >
          {typeLabel}
        </TextView>
        <div>
          <OpenAction isPdf={isPdf} title={title} onOpen={onOpen} />
        </div>
      </Meta>
    </FullCard>
  );
}

export default function DocumentPreviewCard({ doc, previewStyle, isEditPage }) {
  const [inAppPdfUrl, setInAppPdfUrl] = useState(null);
  const src = doc?.src;
  const isPdf = isPdfUrl(src);
  const iconSrc = (doc?.type && fileIcons[doc.type]) || ASSETS.doc;
  const typeLabel = getDocTypeLabel(doc?.type);
  const title = doc?.title || "Document";

  const handleOpen = () => {
    if (isPdf) {
      setInAppPdfUrl(src);
    } else if (!isEditPage) {
      // Non-PDF documents keep the legacy open-externally / new-tab behaviour;
      // suppressed on the builder page (parity with the icon-list render).
      goToUrl(src);
    }
  };

  const facsimile = (
    <PdfFirstPagePreview
      url={src}
      width={FACSIMILE_W}
      fallback={<FallbackIcon src={iconSrc} alt={title} />}
      style={{ borderRadius: 6, boxShadow: "0 1px 4px rgba(18,48,81,0.12)" }}
    />
  );

  const body =
    previewStyle === PREVIEW_STYLES.COVER ? (
      <CoverBody
        chip={getDocTypeChip(doc?.type)}
        facsimile={facsimile}
        title={title}
        typeLabel={typeLabel}
        isPdf={isPdf}
        onOpen={handleOpen}
      />
    ) : (
      <FullBody
        facsimile={facsimile}
        title={title}
        typeLabel={typeLabel}
        isPdf={isPdf}
        onOpen={handleOpen}
      />
    );

  return (
    <>
      {body}
      <PdfViewerModal
        open={Boolean(inAppPdfUrl)}
        url={inAppPdfUrl}
        onClose={() => setInAppPdfUrl(null)}
      />
    </>
  );
}
